'use client';

import React from 'react';
import { Ban } from 'lucide-react';
import PolicyLayout, {
  PolicySection,
  PolicySubSection,
  PolicyList,
  PolicyCallout,
  TocSection,
} from '@/components/PolicyLayout';

const sections: TocSection[] = [
  { id: 'overview', title: 'Overview' },
  { id: 'prohibited-activities', title: 'Prohibited Activities' },
  { id: 'spam', title: 'Spam & Abuse' },
  { id: 'scraping', title: 'Scraping & Data Extraction' },
  { id: 'automation-misuse', title: 'Automation Misuse' },
  { id: 'malicious-uploads', title: 'Malicious Uploads' },
  { id: 'illegal-activity', title: 'Illegal Activity' },
  { id: 'enforcement', title: 'Enforcement' },
  { id: 'reporting', title: 'Reporting Violations' },
];

export default function AcceptableUseClient() {
  return (
    <PolicyLayout
      title="Acceptable Use Policy"
      description="Rules governing acceptable use of JobCompanion to protect users, employers, and the integrity of the job search ecosystem."
      lastUpdated="May 18, 2026"
      icon={Ban}
      iconBg="bg-error/10"
      iconColor="text-error"
      sections={sections}
      currentPath="/acceptable-use"
    >
      <PolicySection id="overview" title="Overview">
        <p>
          JobCompanion is designed to help genuine job seekers find and apply for jobs more effectively. To protect the integrity of the platform and the broader job search ecosystem, we prohibit certain activities that harm users, employers, or the platform itself.
        </p>
        <p>
          This Acceptable Use Policy applies to all users of JobCompanion, including free and paid plan users, beta testers, and any automated systems accessing the platform.
        </p>
        <PolicyCallout type="warning">
          Violations of this policy may result in immediate account suspension, permanent ban, and in serious cases, legal action.
        </PolicyCallout>
      </PolicySection>

      <PolicySection id="prohibited-activities" title="Prohibited Activities">
        <p>The following activities are strictly prohibited on JobCompanion:</p>
      </PolicySection>

      <PolicySection id="spam" title="Spam & Application Abuse">
        <PolicyList items={[
          'Submitting mass, untargeted, or low-quality applications to employers without genuine interest',
          'Using the platform to send unsolicited messages or communications to employers or recruiters',
          'Creating multiple accounts to circumvent usage limits',
          'Using fake or fabricated identity information in job applications',
          'Submitting applications on behalf of other people without their explicit consent',
          'Generating and submitting applications at volumes designed to overwhelm employer systems',
        ]} />
      </PolicySection>

      <PolicySection id="scraping" title="Scraping & Data Extraction">
        <PolicyList items={[
          'Using automated tools, bots, or scripts to scrape job listings from the platform',
          'Extracting user data, employer data, or platform content for commercial purposes',
          'Reverse-engineering the platform\'s matching algorithms or AI models',
          'Accessing the platform\'s API endpoints without authorization',
          'Harvesting email addresses or contact information from job listings',
          'Copying platform content for redistribution or commercial use',
        ]} />
      </PolicySection>

      <PolicySection id="automation-misuse" title="Automation Misuse">
        <PolicyList items={[
          'Attempting to bypass automation safety rules (CAPTCHA blocking, login wall detection, payment detection)',
          'Using automation features to apply to jobs you have no genuine interest in',
          'Configuring automation to circumvent employer bot-detection systems',
          'Using automation to submit applications containing false or misleading information',
          'Sharing automation credentials or workflows with unauthorized third parties',
          'Using the platform\'s automation infrastructure for purposes other than personal job applications',
        ]} />
        <PolicySubSection title="Note on Employer Terms">
          <p>
            Some employers and job platforms prohibit automated applications in their terms of service. You are responsible for ensuring your use of JobCompanion&apos;s automation features complies with the terms of each platform you apply to.
          </p>
        </PolicySubSection>
      </PolicySection>

      <PolicySection id="malicious-uploads" title="Malicious Uploads">
        <PolicyList items={[
          'Uploading files containing malware, viruses, or malicious code',
          'Uploading files designed to exploit vulnerabilities in our document processing pipeline',
          'Uploading content that infringes on third-party intellectual property rights',
          'Uploading content that is defamatory, harassing, or illegal',
          'Uploading files that are not genuine resumes or job-related documents',
          'Uploading files containing personal data of third parties without their consent',
        ]} />
      </PolicySection>

      <PolicySection id="illegal-activity" title="Illegal Activity">
        <PolicyList items={[
          'Using the platform to facilitate identity theft or fraud',
          'Submitting job applications containing forged credentials, fake degrees, or fabricated work history',
          'Using the platform to violate employment discrimination laws',
          'Accessing the platform from jurisdictions where it is prohibited by law',
          'Using the platform to violate export control laws or sanctions',
          'Any activity that violates applicable local, national, or international law',
        ]} />
      </PolicySection>

      <PolicySection id="enforcement" title="Enforcement">
        <p>
          We reserve the right to investigate suspected violations of this policy. Enforcement actions may include:
        </p>
        <PolicyList items={[
          'Warning: A formal notice for minor or first-time violations',
          'Feature restriction: Limiting access to specific features',
          'Account suspension: Temporary suspension pending investigation',
          'Account termination: Permanent ban for serious or repeated violations',
          'Legal action: Referral to law enforcement for illegal activities',
          'Data preservation: Retaining relevant data for legal proceedings',
        ]} />
        <p>
          We will make reasonable efforts to notify you of enforcement actions and provide an opportunity to appeal, except where immediate action is required to protect the platform or other users.
        </p>
      </PolicySection>

      <PolicySection id="reporting" title="Reporting Violations">
        <p>
          If you observe a violation of this policy or encounter suspicious activity, please report it to us at <a href="mailto:safety@jobcompanion.app" className="text-primary underline">safety@jobcompanion.app</a> or through our <a href="/contact-grievance" className="text-primary underline">Contact & Grievance page</a>.
        </p>
        <p>
          Reports are reviewed by our safety team. We take all reports seriously and will investigate promptly. Reporter identity is kept confidential.
        </p>
      </PolicySection>
    </PolicyLayout>
  );
}
